/**
 * Views/Prenomina/IncidenciasPermanentesEmpleado.jsx
 *
 * Contraparte de MovimientosPermanentesEmpleado para el lado de incidencias.
 *
 * Configura incidencias que se capturan solas en los mismos días de cada
 * periodo (ej. "2 horas extra dobles cada sábado"). El patrón se marca sobre
 * la cuadrícula de días del periodo abierto, que es la misma referencia que usa
 * la pestaña "Días e Incidencias".
 *
 * Dos formas de interpretar los días (tipoPatron):
 *   'diaPeriodo' → la posición 1..N del periodo (para "el día 15 de la quincena")
 *   'diaSemana'  → el día de la semana (para "cada sábado"); al marcar una
 *                  celda se marcan todas las del mismo día de la semana.
 *
 * El motor las materializa en LineaPrenomina.incidencias[] al calcular la
 * nómina, sin pisar lo capturado a mano.
 */

import React, { Component } from 'react'
import axios from 'axios'
import moment from 'moment'
import { Row, Col, Tag, Skeleton, Modal, message } from 'antd'
import { connect } from 'react-redux'
import { ButtonItem } from '../../../../Components/Items'
import DatePickerItem from '../../../../Components/DatePickerItem'
import { ExclamationCircleOutlined } from '@ant-design/icons'

// ─── Helpers ──────────────────────────────────────────────────────────────────

const DIAS_ES = ['Dom', 'Lun', 'Mar', 'Mié', 'Jue', 'Vie', 'Sáb']

const fmtFecha = (f) => {
  if (!f) return '—'
  const s = (f || '').split('T')[0]
  const p = s.split('-')
  return p.length === 3 ? `${p[2]}/${p[1]}/${p[0]}` : s
}

const TIPO_PATRON_LABEL = {
  diaPeriodo: 'Día del periodo',
  diaSemana:  'Día de la semana',
}

// TipoIncidencia.tipo — la unidad de la cantidad que se captura
const UNIDAD_POR_TIPO = { 1: 'destajos', 2: 'días', 3: 'horas' }

const getTipoIncidenciaId = (inc) => inc.tipoIncidenciaId?._id || inc.tipoIncidenciaId || ''

const nombreTipoIncidencia = (inc) => {
  const t = inc.tipoIncidenciaId
  if (!t) return '—'
  if (typeof t === 'object') return `${t.clave} - ${t.nombre}`
  return t
}

/** Texto corto del patrón para la tabla: "Sáb: 2 · Dom: 1" / "Día 3: 2" */
const resumenPatron = (inc) => {
  const dias = inc.dias || []
  if (!dias.length) return '—'
  return dias
    .map(d => `${inc.tipoPatron === 'diaSemana' ? (DIAS_ES[d.clave] || d.clave) : `Día ${d.clave}`}: ${d.cantidad}`)
    .join(' · ')
}

// ─── Estilos tabla ────────────────────────────────────────────────────────────

const TH = {
  border: '1px solid #d0d0d0', background: '#f5f5f5',
  padding: '5px 8px', fontSize: 11, fontWeight: 600,
  color: '#555', textAlign: 'center', whiteSpace: 'nowrap',
}
const TD = {
  border: '1px solid #e8e8e8', padding: '4px 8px',
  fontSize: 12, verticalAlign: 'middle',
}
const TD_NUM = { ...TD, textAlign: 'right', fontFamily: 'monospace' }

// Cuadrícula de días (mismos estilos compactos que IncidenciasTab)
const TH_DIA = {
  border: '1px solid #d0d0d0', background: '#f5f5f5',
  padding: '4px 6px', fontSize: 11, fontWeight: 600,
  color: '#555', textAlign: 'center', whiteSpace: 'nowrap',
}
const TD_DIA = {
  border: '1px solid #e8e8e8', padding: '2px 4px',
  fontSize: 11, textAlign: 'center', verticalAlign: 'middle',
}

// ─── Estilos formulario ───────────────────────────────────────────────────────

const labelStyle    = { display: 'block', fontSize: 11, color: '#888', marginBottom: 3 }
const inputStyle    = { width: '100%', padding: '4px 8px', border: '1px solid #d0d0d0', borderRadius: 4, fontSize: 12, boxSizing: 'border-box' }
const selectStyle   = { ...inputStyle, background: 'white', cursor: 'pointer', height: 28 }
const btnSmallStyle = { fontSize: 11, padding: '2px 8px', borderRadius: 4, border: '1px solid', cursor: 'pointer', background: 'white' }

// ─────────────────────────────────────────────────────────────────────────────

class IncidenciasPermanentesEmpleado extends Component {

  state = {
    cargando: false,
    error: null,
    incidencias: [],
    // Formulario
    formVisible: false,
    editandoId: null,
    descripcion: '',
    tipoIncidenciaId: '',
    tipoPatron: 'diaPeriodo',
    // celdas[clave] = cantidad — clave es posición del periodo o día de semana
    celdas: {},
    fechaInicio: '',
    fechaFin: '',
    guardando: false,
    errorDuplicado: null,
  }

  componentDidMount() {
    this.cargarDatos()
  }

  componentDidUpdate(prevProps) {
    if (
      prevProps.empleadoId !== this.props.empleadoId ||
      prevProps.empresaId  !== this.props.empresaId
    ) {
      this.resetForm()
      this.cargarDatos()
    }
  }

  resetForm() {
    this.setState({
      formVisible: false, editandoId: null,
      descripcion: '', tipoIncidenciaId: '', tipoPatron: 'diaPeriodo',
      celdas: {}, fechaInicio: '', fechaFin: '',
      errorDuplicado: null,
    })
  }

  // ── Fetch ──────────────────────────────────────────────────────────────────

  async cargarDatos() {
    const { empleadoId, empresaId, headersToken } = this.props
    if (!empleadoId || !empresaId) return
    this.setState({ cargando: true, error: null })
    try {
      const res = await axios.post(
        'nomina/incidenciasPermanentes',
        { empleadoId, empresaId },
        { headers: headersToken }
      )
      if (res.data.success === 2) {
        this.setState({ incidencias: res.data.incidencias || [] })
      } else {
        this.setState({ error: res.data.err || res.data.mensaje || 'Error al cargar incidencias permanentes' })
      }
    } catch {
      this.setState({ error: 'Error de conexión' })
    }
    this.setState({ cargando: false })
  }

  // ── Días del periodo (misma base que IncidenciasTab) ───────────────────────

  getDias() {
    const { fechaInicio, fechaFin } = this.props.prenomina || {}
    if (!fechaInicio || !fechaFin) return []
    const n = moment(fechaFin).diff(moment(fechaInicio), 'day') + 1
    if (n <= 0) return []
    return Array.from({ length: n }, (_, i) => i + 1)
  }

  getFechaDelDia(diaPeriodo) {
    const inicio = this.props.prenomina?.fechaInicio
    if (!inicio) return null
    return moment(inicio).add(diaPeriodo - 1, 'day')
  }

  /** La clave con la que se guarda ese día según el tipo de patrón */
  getClaveDia(diaPeriodo) {
    if (this.state.tipoPatron === 'diaSemana') {
      const fecha = this.getFechaDelDia(diaPeriodo)
      return fecha ? fecha.day() : null
    }
    return diaPeriodo
  }

  /**
   * Guarda lo que el usuario va escribiendo TAL CUAL (texto), sin parsear.
   * Si se parseara en cada tecla, "1.5" sería imposible de teclear: al llegar
   * al punto, parseFloat('1.') devuelve 1 y el punto se perdería. Se normaliza
   * al salir de la celda (normalizarCelda).
   */
  setCeldaTexto(diaPeriodo, texto) {
    const clave = this.getClaveDia(diaPeriodo)
    if (clave === null) return
    this.setState(prev => {
      const celdas = { ...prev.celdas }
      if (texto === '') delete celdas[clave]
      else celdas[clave] = texto
      return { celdas }
    })
  }

  normalizarCelda(diaPeriodo) {
    const clave = this.getClaveDia(diaPeriodo)
    if (clave === null) return
    this.setState(prev => {
      const celdas = { ...prev.celdas }
      const cantidad = parseFloat(celdas[clave])
      if (isNaN(cantidad) || cantidad <= 0) delete celdas[clave]
      else celdas[clave] = cantidad
      return { celdas }
    })
  }

  getCelda(diaPeriodo) {
    const clave = this.getClaveDia(diaPeriodo)
    if (clave === null) return ''
    return this.state.celdas[clave] ?? ''
  }

  cambiarTipoPatron(tipoPatron) {
    // Las claves significan cosas distintas en cada modo (posición vs día de
    // semana), así que el patrón capturado no se puede reinterpretar: se limpia.
    if (tipoPatron === this.state.tipoPatron) return
    this.setState({ tipoPatron, celdas: {} })
  }

  // ── Acciones ───────────────────────────────────────────────────────────────

  confirmarInactivar(incidenciaId) {
    Modal.confirm({
      title: '¿Inactivar esta incidencia permanente?',
      content: 'Dejará de capturarse sola. En los periodos abiertos sus días se quitan al volver a calcular la nómina.',
      icon: <ExclamationCircleOutlined />,
      okText: 'Sí, inactivar',
      cancelText: 'No',
      onOk: () => this.inactivarIncidencia(incidenciaId),
    })
  }

  async inactivarIncidencia(incidenciaId) {
    const { empresaId, headersToken } = this.props
    try {
      const res = await axios.post(
        'nomina/cancelarIncidenciaPermanente',
        { incidenciaId, empresaId },
        { headers: headersToken }
      )
      if (res.data.success === 2) {
        message.success('Incidencia permanente inactivada. Presiona "Calcular todo" para reflejarlo en el periodo.')
        this.cargarDatos()
        this.props.onCambio?.()
      } else {
        message.error(res.data.err || res.data.mensaje || 'Error al inactivar')
      }
    } catch {
      message.error('Error de conexión')
    }
  }

  abrirEdicion(inc) {
    const celdas = {}
    for (const d of inc.dias || []) celdas[d.clave] = d.cantidad

    this.setState({
      formVisible: true,
      editandoId: inc._id,
      descripcion: inc.descripcion || '',
      tipoIncidenciaId: getTipoIncidenciaId(inc),
      tipoPatron: inc.tipoPatron || 'diaPeriodo',
      celdas,
      fechaInicio: (inc.fechaInicio || '').split('T')[0],
      fechaFin: (inc.fechaFin || '').split('T')[0],
      errorDuplicado: null,
    })
  }

  abrirFormularioNuevo() {
    // Por defecto arranca vigente desde el inicio del periodo abierto
    const inicioPeriodo = this.props.prenomina?.fechaInicio
    this.setState({
      formVisible: true, editandoId: null,
      descripcion: '', tipoIncidenciaId: '', tipoPatron: 'diaPeriodo',
      celdas: {},
      fechaInicio: inicioPeriodo ? moment(inicioPeriodo).format('YYYY-MM-DD') : '',
      fechaFin: '',
      errorDuplicado: null,
    })
  }

  validarDuplicado(tipoIncidenciaId) {
    const { editandoId, incidencias } = this.state
    if (!tipoIncidenciaId) return null
    const duplicado = incidencias.find(i =>
      i.estatus === 1 &&
      getTipoIncidenciaId(i) === tipoIncidenciaId &&
      i._id !== editandoId
    )
    return duplicado ? 'Esta incidencia ya tiene una configuración permanente activa' : null
  }

  async guardarIncidencia() {
    const { empleadoId, empresaId, headersToken } = this.props
    const {
      descripcion, tipoIncidenciaId, tipoPatron, celdas,
      fechaInicio, fechaFin, editandoId,
    } = this.state

    if (!tipoIncidenciaId) { message.error('Selecciona el tipo de incidencia'); return }
    if (!fechaInicio)      { message.error('Ingresa la fecha de inicio');       return }

    const dias = Object.entries(celdas)
      .map(([clave, cantidad]) => ({ clave: parseInt(clave, 10), cantidad: parseFloat(cantidad) }))
      .filter(d => Number.isInteger(d.clave) && d.cantidad > 0)
      .sort((a, b) => a.clave - b.clave)

    if (!dias.length) { message.error('Marca al menos un día con cantidad mayor a 0'); return }
    if (fechaFin && moment(fechaFin).isBefore(moment(fechaInicio), 'day')) {
      message.error('La fecha fin no puede ser anterior a la fecha de inicio'); return
    }

    const errDuplicado = this.validarDuplicado(tipoIncidenciaId)
    if (errDuplicado) { this.setState({ errorDuplicado: errDuplicado }); return }

    const body = {
      incidenciaId: editandoId || undefined,
      empleadoId, empresaId,
      tipoIncidenciaId,
      descripcion: descripcion.trim(),
      tipoPatron, dias,
      fechaInicio,
      fechaFin: fechaFin || null,
    }

    this.setState({ guardando: true })
    try {
      const endpoint = editandoId
        ? 'nomina/editarIncidenciaPermanente'
        : 'nomina/crearIncidenciaPermanente'
      const res = await axios.post(endpoint, body, { headers: headersToken })
      if (res.data.success === 2) {
        message.success(
          `${editandoId ? 'Incidencia permanente actualizada' : 'Incidencia permanente registrada'}. Presiona "Calcular todo" para capturarla en el periodo.`,
          6
        )
        this.resetForm()
        this.cargarDatos()
        this.props.onCambio?.()
      } else {
        message.error(res.data.err || res.data.mensaje || 'Error al guardar')
      }
    } catch {
      message.error('Error de conexión')
    }
    this.setState({ guardando: false })
  }

  // ── Helpers de render ──────────────────────────────────────────────────────

  getTipoSeleccionado() {
    return (this.props.tiposIncidencias || [])
      .find(t => t._id === this.state.tipoIncidenciaId)
  }

  getTextoAyudaPatron() {
    const { tipoPatron, celdas } = this.state
    const marcados = Object.keys(celdas).length
    if (!marcados) return { text: 'Marca la cantidad en los días que apliquen', color: '#888' }

    // Las celdas pueden traer el texto crudo mientras se escribe
    const total = Object.values(celdas).reduce((s, v) => s + (parseFloat(v) || 0), 0)
    const unidad = UNIDAD_POR_TIPO[this.getTipoSeleccionado()?.tipo] || 'unidades'

    if (tipoPatron === 'diaSemana') {
      const nombres = Object.keys(celdas).map(c => DIAS_ES[c] || c).join(', ')
      return {
        text: `Se capturará cada ${nombres} del periodo (${total} ${unidad} por semana marcada)`,
        color: '#1677ff',
      }
    }
    return {
      text: `Se capturará en ${marcados} día(s) del periodo, ${total} ${unidad} en total`,
      color: '#1677ff',
    }
  }

  renderCards() {
    const { incidencias } = this.state
    const activas   = incidencias.filter(i => i.estatus === 1).length
    const inactivas = incidencias.filter(i => i.estatus === 0).length

    return (
      <Row gutter={12} style={{ marginBottom: 16 }}>
        <Col span={12}>
          <div style={{ background: '#f6ffed', borderRadius: 6, padding: '10px 14px' }}>
            <div style={{ fontSize: 11, color: '#3B6D11' }}>Activas</div>
            <div style={{ fontSize: 22, fontWeight: 700, color: '#3B6D11' }}>{activas}</div>
          </div>
        </Col>
        <Col span={12}>
          <div style={{ background: '#f5f5f5', borderRadius: 6, padding: '10px 14px' }}>
            <div style={{ fontSize: 11, color: '#595959' }}>Inactivas</div>
            <div style={{ fontSize: 22, fontWeight: 700, color: '#595959' }}>{inactivas}</div>
          </div>
        </Col>
      </Row>
    )
  }

  renderTabla() {
    const { incidencias } = this.state
    const { timbrado } = this.props

    const colCount = timbrado ? 7 : 8

    return (
      <div style={{ overflowX: 'auto' }}>
        <table style={{ borderCollapse: 'collapse', tableLayout: 'auto', minWidth: 820 }}>
          <thead>
            <tr>
              <th style={{ ...TH, minWidth: 140 }}>Descripción</th>
              <th style={{ ...TH, minWidth: 150 }}>Incidencia</th>
              <th style={TH}>Patrón</th>
              <th style={{ ...TH, minWidth: 180 }}>Días</th>
              <th style={TH}>Total</th>
              <th style={TH}>Vigencia</th>
              <th style={TH}>Estatus</th>
              {!timbrado && <th style={TH}>Acciones</th>}
            </tr>
          </thead>
          <tbody>
            {incidencias.length === 0 && (
              <tr>
                <td colSpan={colCount} style={{ ...TD, textAlign: 'center', color: '#aaa', padding: 24 }}>
                  Sin incidencias permanentes registradas
                </td>
              </tr>
            )}
            {incidencias.map(inc => {
              const activa = inc.estatus === 1
              const esEditable = activa && !timbrado
              const total = (inc.dias || []).reduce((s, d) => s + (d.cantidad || 0), 0)

              const rowBg = activa ? 'white' : '#f5f5f5'
              const textStyle = activa ? {} : { textDecoration: 'line-through', color: '#aaa' }

              return (
                <tr key={inc._id} style={{ background: rowBg }}>
                  <td style={{ ...TD, ...textStyle, maxWidth: 170, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {inc.descripcion || '—'}
                  </td>
                  <td style={{ ...TD, ...textStyle, maxWidth: 180, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {nombreTipoIncidencia(inc)}
                  </td>
                  <td style={{ ...TD, ...textStyle }}>
                    {TIPO_PATRON_LABEL[inc.tipoPatron] || inc.tipoPatron}
                  </td>
                  <td style={{ ...TD, ...textStyle, maxWidth: 220 }}>{resumenPatron(inc)}</td>
                  <td style={{ ...TD_NUM, ...textStyle }}>{total || '—'}</td>
                  <td style={{ ...TD, ...textStyle, whiteSpace: 'nowrap' }}>
                    {fmtFecha(inc.fechaInicio)} → {inc.fechaFin ? fmtFecha(inc.fechaFin) : '∞'}
                  </td>
                  <td style={{ ...TD, textAlign: 'center' }}>
                    {activa
                      ? <Tag color="green">Activa</Tag>
                      : <Tag color="default" style={{ color: '#aaa' }}>Inactiva</Tag>}
                  </td>
                  {!timbrado && (
                    <td style={{ ...TD, textAlign: 'center', whiteSpace: 'nowrap' }}>
                      {esEditable && (
                        <>
                          <button type="button"
                            onClick={() => this.abrirEdicion(inc)}
                            style={{ ...btnSmallStyle, color: '#1677ff', borderColor: '#1677ff' }}
                          >
                            Editar
                          </button>
                          {' '}
                          <button type="button"
                            onClick={() => this.confirmarInactivar(inc._id)}
                            style={{ ...btnSmallStyle, color: '#ff4d4f', borderColor: '#ff4d4f' }}
                          >
                            Inactivar
                          </button>
                        </>
                      )}
                    </td>
                  )}
                </tr>
              )
            })}
          </tbody>
        </table>
      </div>
    )
  }

  /** Cuadrícula de los días del periodo — un input por día */
  renderCuadriculaDias() {
    const { tipoPatron } = this.state
    const dias = this.getDias()

    if (!dias.length) {
      return (
        <div style={{ padding: '10px 14px', background: '#fffbe6', border: '1px solid #ffe58f', borderRadius: 6, fontSize: 12, color: '#7c5a00' }}>
          El periodo de esta nómina no tiene fechas, no se pueden dibujar los días.
        </div>
      )
    }

    return (
      <div style={{ overflowX: 'auto' }}>
        <table style={{ borderCollapse: 'collapse', tableLayout: 'auto' }}>
          <thead>
            <tr>
              <th style={{ ...TH_DIA, textAlign: 'left', minWidth: 110, position: 'sticky', left: 0, zIndex: 2, background: '#f5f5f5' }}>
                {tipoPatron === 'diaSemana' ? 'Día de semana' : 'Día del periodo'}
              </th>
              {dias.map(d => {
                const fecha = this.getFechaDelDia(d)
                const esFin = fecha && (fecha.day() === 0 || fecha.day() === 6)
                return (
                  <th key={d} style={{ ...TH_DIA, minWidth: 46, background: esFin ? '#fff7e6' : '#f5f5f5' }}>
                    <div>{fecha ? fecha.format('DD') : d}</div>
                    {fecha && (
                      <div style={{ fontSize: 9, color: esFin ? '#faad14' : '#aaa', fontWeight: 400 }}>
                        {DIAS_ES[fecha.day()]}
                      </div>
                    )}
                    <div style={{ fontSize: 9, color: '#ccc', fontWeight: 400 }}>{d}</div>
                  </th>
                )
              })}
            </tr>
          </thead>
          <tbody>
            <tr>
              <td style={{
                ...TD_DIA, textAlign: 'left', fontWeight: 500,
                background: '#fafafa', position: 'sticky', left: 0,
                borderRight: '2px solid #d0d0d0',
              }}>
                Cantidad
              </td>
              {dias.map(d => {
                const fecha = this.getFechaDelDia(d)
                const esFin = fecha && (fecha.day() === 0 || fecha.day() === 6)
                const val = this.getCelda(d)

                return (
                  <td key={d} style={{ ...TD_DIA, background: esFin ? '#fff7e6' : 'white' }}>
                    <input
                      type="number"
                      min={0}
                      step="0.01"
                      value={val}
                      placeholder="—"
                      style={{
                        width: 36, textAlign: 'center', border: 'none',
                        background: 'transparent', fontSize: 11,
                        color: val > 0 ? '#1677ff' : '#ccc',
                        fontWeight: val > 0 ? 700 : 400,
                        outline: 'none',
                      }}
                      onChange={e => this.setCeldaTexto(d, e.target.value)}
                      onBlur={() => this.normalizarCelda(d)}
                    />
                  </td>
                )
              })}
            </tr>
          </tbody>
        </table>
      </div>
    )
  }

  renderFormulario() {
    const {
      descripcion, tipoIncidenciaId, tipoPatron,
      fechaInicio, fechaFin, editandoId, guardando, errorDuplicado,
    } = this.state
    const { tiposIncidencias } = this.props

    const { text: textoAyuda, color: colorAyuda } = this.getTextoAyudaPatron()
    const tipoSel = this.getTipoSeleccionado()

    return (
      <div style={{ border: '1px solid #d0d0d0', borderRadius: 6, padding: 16, marginBottom: 16, background: '#fafcff' }}>
        <div style={{ fontWeight: 600, marginBottom: 12, color: '#555', fontSize: 13 }}>
          {editandoId ? 'Editar incidencia permanente' : 'Nueva incidencia permanente'}
        </div>

        {/* Descripción */}
        <div style={{ marginBottom: 12 }}>
          <label style={labelStyle}>Descripción</label>
          <input
            type="text"
            value={descripcion}
            placeholder="Ej. Horas extra fijas de sábado"
            onChange={e => this.setState({ descripcion: e.target.value })}
            style={inputStyle}
          />
        </div>

        {/* Tipo de incidencia + tipo de patrón */}
        <div style={{ display: 'flex', gap: 12, marginBottom: 12 }}>
          <div style={{ flex: 2 }}>
            <label style={labelStyle}>Tipo de incidencia</label>
            <select
              value={tipoIncidenciaId}
              onChange={e => {
                const val = e.target.value
                this.setState({
                  tipoIncidenciaId: val,
                  errorDuplicado: this.validarDuplicado(val),
                })
              }}
              style={selectStyle}
            >
              <option value="">— Seleccionar —</option>
              {(tiposIncidencias || []).map(t => (
                <option key={t._id} value={t._id}>
                  {`${t.clave} - ${t.nombre}`}
                </option>
              ))}
            </select>
            {tipoSel && (
              <div style={{ fontSize: 11, color: '#888', marginTop: 3 }}>
                La cantidad se captura en {UNIDAD_POR_TIPO[tipoSel.tipo] || 'unidades'}
                {tipoSel.claveTipoHorasSat ? ` · Horas extra SAT ${tipoSel.claveTipoHorasSat}` : ''}
              </div>
            )}
            {errorDuplicado && (
              <div style={{ color: '#A32D2D', fontSize: 11, marginTop: 3 }}>
                {errorDuplicado}
              </div>
            )}
          </div>
          <div style={{ flex: 1 }}>
            <label style={labelStyle}>Repetir por</label>
            <select
              value={tipoPatron}
              onChange={e => this.cambiarTipoPatron(e.target.value)}
              style={selectStyle}
            >
              <option value="diaPeriodo">Día del periodo (posición)</option>
              <option value="diaSemana">Día de la semana</option>
            </select>
            <div style={{ fontSize: 11, color: '#888', marginTop: 3 }}>
              {tipoPatron === 'diaSemana'
                ? 'Cae en todos los días de la semana marcados, aunque el periodo cambie'
                : 'Cae siempre en la misma posición del periodo (día 1, 2, 3...)'}
            </div>
          </div>
        </div>

        {/* Cuadrícula de días */}
        <div style={{ marginBottom: 12 }}>
          <label style={labelStyle}>
            Días del periodo
            {tipoPatron === 'diaSemana' && ' — al marcar un día se marcan todos los del mismo día de la semana'}
          </label>
          {this.renderCuadriculaDias()}
          <div style={{ fontSize: 11, color: colorAyuda, marginTop: 6 }}>{textoAyuda}</div>
        </div>

        {/* Vigencia */}
        <div style={{ display: 'flex', gap: 12, marginBottom: 12 }}>
          <div style={{ flex: 1 }}>
            <label style={labelStyle}>Vigente desde</label>
            <DatePickerItem
              value={fechaInicio ? moment(fechaInicio, 'YYYY-MM-DD') : null}
              onChange={(d) => this.setState({ fechaInicio: d ? d.format('YYYY-MM-DD') : '' })}
              style={{ ...inputStyle, width: '100%' }}
            />
          </div>
          <div style={{ flex: 1 }}>
            <label style={labelStyle}>Vigente hasta (opcional)</label>
            <DatePickerItem
              value={fechaFin ? moment(fechaFin, 'YYYY-MM-DD') : null}
              onChange={(d) => this.setState({ fechaFin: d ? d.format('YYYY-MM-DD') : '' })}
              style={{ ...inputStyle, width: '100%' }}
            />
          </div>
        </div>

        {/* Botones */}
        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 4 }}>
          <ButtonItem size="small" onClick={() => this.resetForm()}>
            Cancelar captura
          </ButtonItem>
          <ButtonItem type="primary" size="small" loading={guardando} onClick={() => this.guardarIncidencia()}>
            Guardar
          </ButtonItem>
        </div>
      </div>
    )
  }

  render() {
    const { cargando, error, formVisible } = this.state
    const { timbrado, readOnly, tiposIncidencias } = this.props

    if (cargando) return <Skeleton active paragraph={{ rows: 4 }} />

    if (error) {
      return (
        <div style={{ color: '#A32D2D', padding: '10px 14px', background: '#fff1f0', borderRadius: 4, fontSize: 12 }}>
          {error}
        </div>
      )
    }

    const puedeEditar = !readOnly && !timbrado
    const sinCatalogo = !(tiposIncidencias || []).length

    return (
      <div>
        {timbrado && (
          <div style={{ marginBottom: 12, padding: '7px 14px', background: '#fffbe6', border: '1px solid #ffe58f', borderRadius: 6, fontSize: 12, color: '#7c5a00' }}>
            Nómina timbrada — visualización de solo lectura.
          </div>
        )}

        <p style={{ fontSize: 12, color: '#888', marginBottom: 12 }}>
          Incidencias que se capturan solas en los mismos días de cada periodo, sin tener que
          escribirlas en <b>Días e Incidencias</b>. Si un día ya tiene esa incidencia capturada
          a mano o importada del reloj checador, <b>gana lo capturado</b>: la permanente no lo pisa.
          Cambiar la cantidad de una celda en Días e Incidencias la deja fija para ese periodo;
          para que no aplique en un día, quita ese día de la configuración.
        </p>

        {this.renderCards()}

        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
          <div style={{ fontWeight: 600, fontSize: 13, color: '#555' }}>Incidencias permanentes</div>
          {puedeEditar && !formVisible && !sinCatalogo && (
            <ButtonItem size="small" type="primary" onClick={() => this.abrirFormularioNuevo()}>
              + Agregar incidencia
            </ButtonItem>
          )}
        </div>

        {sinCatalogo && (
          <div style={{ marginBottom: 12, padding: '7px 14px', background: '#fffbe6', border: '1px solid #ffe58f', borderRadius: 6, fontSize: 12, color: '#7c5a00' }}>
            No hay tipos de incidencia configurados en el catálogo.
          </div>
        )}

        {formVisible && this.renderFormulario()}
        {this.renderTabla()}
      </div>
    )
  }
}

const mapStateToProps = (state) => ({
  headersToken: state.user.headersToken,
  empresaId:    state.empresa.id,
})

export default connect(mapStateToProps)(IncidenciasPermanentesEmpleado)
